import { motion } from "framer-motion";
import { TrendingDown } from "lucide-react";
import SceneShell from "../components/SceneShell";
import CountUp from "../components/CountUp";
import MoneyFlow from "../components/MoneyFlow";

/**
 * Scene 26 — Impact moment · £97,000 saved per year.
 * Big bold reveal with money particles flowing upward.
 */

export default function Scene26_Saving97k() {
  return (
    <SceneShell variant="ink" align="center" data-testid="scene-saving-97k">
      {/* Money flow particles in the background */}
      <MoneyFlow density={36} color="rgba(150,193,31,0.85)" />
      <MoneyFlow density={20} color="rgba(0,222,182,0.65)" />

      {/* Pulsing lime glow */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          background: [
            "radial-gradient(ellipse at center, rgba(150,193,31,0.20) 0%, transparent 55%)",
            "radial-gradient(ellipse at center, rgba(150,193,31,0.32) 0%, transparent 55%)",
            "radial-gradient(ellipse at center, rgba(150,193,31,0.20) 0%, transparent 55%)",
          ],
        }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative max-w-[1500px] mx-auto w-full text-center">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="kn-eyebrow mx-auto flex items-center justify-center gap-2"
        >
          <TrendingDown className="w-3.5 h-3.5" />
          Big Impact
        </motion.div>

        {/* The big number */}
        <motion.div
          initial={{ opacity: 0, scale: 0.65, filter: "blur(28px)" }}
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          transition={{ duration: 1.6, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10"
          data-testid="saving-97k-number"
        >
          <div
            className="kn-display tnum leading-[0.85]"
            style={{
              fontSize: "clamp(100px, 16vw, 240px)",
              background:
                "linear-gradient(135deg, #96C11F 0%, #00deb6 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              textShadow: "0 0 100px rgba(150,193,31,0.45)",
              whiteSpace: "nowrap",
            }}
          >
            <CountUp
              from={0}
              to={97000}
              prefix="£"
              duration={2.6}
              startDelay={1.4}
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 2.4 }}
          className="mt-6 kn-display text-3xl sm:text-5xl lg:text-6xl text-white"
        >
          <span className="text-white/55">saved</span>{" "}
          <span style={{ color: "#96C11F" }}>per year.</span>
        </motion.div>

        {/* Before / after comparison strip */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 3.0 }}
          className="mt-12 inline-flex items-center gap-4 rounded-full px-6 py-3 glass-dark"
        >
          <span className="text-xs tracking-[0.32em] uppercase text-white/55">
            Before
          </span>
          <span
            className="text-xl tnum line-through"
            style={{ color: "rgba(200,58,79,0.85)", fontWeight: 700 }}
          >
            £100,000
          </span>
          <span className="text-white/30">→</span>
          <span className="text-xs tracking-[0.32em] uppercase text-white/55">
            After
          </span>
          <span
            className="text-xl tnum"
            style={{ color: "#96C11F", fontWeight: 700 }}
          >
            £3,000
          </span>
        </motion.div>
      </div>
    </SceneShell>
  );
}
